const STORAGE_SUFFIX = '__session_tmp_features';

/**
 * Get session feature flags for a specific project
 */
export function getSessionFeatureFlags(projectName: string | null): Record<string, boolean> {
  if (!projectName) return {};
  const key = `${projectName}${STORAGE_SUFFIX}`;
  const stored = sessionStorage.getItem(key);
  if (!stored) return {};
  try {
    return JSON.parse(stored);
  } catch {
    return {};
  }
}

/**
 * Check if a feature is enabled via session storage
 * Reads project name from localStorage for bootstrap-time evaluation
 */
export function isSessionFeatureEnabled(featureName: string): boolean {
  const projectName = localStorage.getItem('manager-studio-selected-project-name');
  if (!projectName) return false;
  return getSessionFeatureFlags(projectName)[featureName] === true;
}

/**
 * Toggle a session feature flag
 */
export function toggleSessionFeature(projectName: string, featureName: string): boolean {
  const flags = getSessionFeatureFlags(projectName);
  const newState = !flags[featureName];
  flags[featureName] = newState;
  const key = `${projectName}${STORAGE_SUFFIX}`;
  sessionStorage.setItem(key, JSON.stringify(flags));
  return newState;
}

/**
 * Check if a feature is explicitly disabled via session storage
 * Used for opt-out semantics where feature is enabled by default
 */
export function isSessionFeatureDisabled(featureName: string): boolean {
  const projectName = localStorage.getItem('manager-studio-selected-project-name');
  if (!projectName) return false;
  return getSessionFeatureFlags(projectName)[featureName] === false;
}

/**
 * Set a feature to disabled state
 * Used for opt-out toggle where users can temporarily disable a default-enabled feature
 */
export function setSessionFeatureDisabled(projectName: string, featureName: string, disabled: boolean): void {
  const flags = getSessionFeatureFlags(projectName);
  if (disabled) {
    flags[featureName] = false;
  } else {
    delete flags[featureName]; // Remove entry to restore default behavior
  }
  const key = `${projectName}${STORAGE_SUFFIX}`;
  sessionStorage.setItem(key, JSON.stringify(flags));
}
